import { Checkbox, DatePicker, Input, Modal } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo } from "react";
import * as Yup from "yup";

import { useGetGroupUsers2, useGetSavingsById } from "../../api/api.get";

import { useAuthContext } from "../../providers/auth.provider";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";

import { useUpdateSavings } from "../../api/api.update";
import { ISavings } from "../../types/types";

export default function EditSavingsDialog({
  open,
  onCancel,
  onSuccess,
  onError,
  savings,
}: {
  savings?: ISavings;
  open: boolean;
  onCancel: () => void;
  onError: () => void;
  onSuccess: () => void;
}) {
  const { user } = useAuthContext();

  const { defaultGroupId } = useGroupContext();

  const { currency } = useCurrencyContext();

  const { data: savingsData } = useGetSavingsById(
    savings?.groupId,
    savings?.id,
  );

  const { data: groupUsers } = useGetGroupUsers2(defaultGroupId);

  const { mutateAsync, isPending } = useUpdateSavings(
    defaultGroupId,
    savings?.id,
  );

  const formik = useFormik({
    initialValues: {
      desc: savingsData?.description ?? "",

      date: savingsData?.savingsDate
        ? dayjs(savingsData.savingsDate)
        : undefined,

      amount: savingsData?.amount ?? 0,

      usernames: savingsData?.users ?? [],
    },

    enableReinitialize: true,

    validationSchema: Yup.object({
      desc: Yup.string().required("Please enter description"),

      date: Yup.mixed().required("Please select date"),

      amount: Yup.number().required("Please enter number"),
    }),

    onSubmit: async (values) => {
      mutateAsync({
        savingsId: savings!.id,
        savingsDate: values.date!.format("YYYY-MM-DD"),
        description: values.desc,
        amount: +values.amount,
        users: values.usernames,
      })
        .then(() => {
          console.log("Success");
          onSuccess();
        })
        .catch((err) => {
          console.error(err);
          onError();
        });
    },
  });

  const userOptions = useMemo(() => {
    if (!groupUsers) return [];

    return groupUsers
      .filter((gu) => gu.username !== user?.username)
      .map((gu) => ({
        label: gu.firstName,
        value: gu.username,
      }));
  }, [groupUsers, user]);

  return (
    <form className="space-y-6" onSubmit={formik.handleSubmit}>
      <Modal
        title="Edit Savings"
        open={open}
        onOk={formik.submitForm}
        onCancel={onCancel}
        okText="Save"
        okButtonProps={{
          loading: isPending,
        }}
      >
        <div className="flex flex-col gap-6 my-6">
          {/* Savings Name */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Savings Name *
            </label>

            <Input
              placeholder="e.g., Emergency Fund"
              {...formik.getFieldProps("desc")}
              status={formik.errors.desc ? "error" : undefined}
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Created Date *
            </label>

            <DatePicker
              className="w-full"
              value={formik.values.date}
              onChange={(date) => formik.setFieldValue("date", date)}
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium mb-2">Amount *</label>

            <Input
              prefix={currency}
              type="number"
              placeholder="100.00"
              {...formik.getFieldProps("amount")}
              status={formik.errors.amount ? "error" : undefined}
            />
          </div>

          {/* Share Users */}
          {userOptions.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-3">Share With?</p>

              <Checkbox.Group
                options={userOptions}
                value={formik.values.usernames}
                onChange={(values) => formik.setFieldValue("usernames", values)}
              />
            </div>
          )}
        </div>
      </Modal>
    </form>
  );
}
